/**
 * Bundle Builder
 * ----------------------------------------------------------------
 * Lets a customer build a multi-item bundle (e.g. Buy 2 / Buy 3 / Buy 5)
 * from a single product's variants, choosing Color + Size independently
 * for EACH slot, then adds every selected variant to the cart at once.
 *
 * Color swatches render the merchant's actual variant image (passed in
 * from Liquid as `colorSwatches`). If no image exists for a color, that
 * option safely falls back to a plain text pill — never breaks.
 *
 * IMPORTANT: this class is registered as <bundle-builder>. The markup
 * in blocks/bundle-builder.liquid must use that tag as the root element
 * (not a plain <div>), otherwise connectedCallback() never runs.
 */
class BundleBuilder extends HTMLElement {
  constructor() {
    super();
    this.variants = [];
    this.optionNames = [];
    this.colorSwatches = new Map();
  }

  connectedCallback() {
    const dataEl = this.querySelector('[data-bundle-product-data]');
    if (!dataEl) return;

    try {
      const payload = JSON.parse(dataEl.textContent);
      this.variants = Array.isArray(payload.variants) ? payload.variants : [];
      this.optionNames = Array.isArray(payload.options) ? payload.options : [];

      const swatches = Array.isArray(payload.colorSwatches) ? payload.colorSwatches : [];
      this.colorSwatches = new Map(
        swatches.filter((item) => item && item.image).map((item) => [item.value, item.image])
      );
    } catch (error) {
      console.error('BundleBuilder: could not parse product data', error);
      return;
    }

    this.itemLabel = this.dataset.itemLabel || 'Item';

    this.slotsContainer = this.querySelector('.bundle-slots');
    this.planButtons = [...this.querySelectorAll('.bundle-plan')];
    this.addButton = this.querySelector('.bundle-add-button');
    this.errorEl = this.querySelector('.bundle-builder__error');
    this.progressEl = this.querySelector('[data-bundle-progress]');

    if (!this.slotsContainer || !this.addButton) return;

    this.planButtons.forEach((button) => {
      button.addEventListener('click', () => this.selectPlan(button));
    });

    this.addButton.addEventListener('click', () => this.addBundle());

    const initialPlan =
      this.planButtons.find((button) => button.classList.contains('active')) || this.planButtons[0];

    if (initialPlan) this.selectPlan(initialPlan);
  }

  selectPlan(button) {
    this.planButtons.forEach((btn) => {
      btn.classList.remove('active');
      btn.setAttribute('aria-pressed', 'false');
    });

    button.classList.add('active');
    button.setAttribute('aria-pressed', 'true');

    const quantity = Number(button.dataset.quantity) || 1;
    this.createSlots(quantity);
    this.clearError();
  }

  /** Builds N independent slots, each with its own Color/Size buttons. */
  createSlots(quantity) {
    this.slotsContainer.innerHTML = '';

    const colorOptions = this.getOptionValues('Color');
    const sizeOptions = this.getOptionValues('Size');

    for (let i = 1; i <= quantity; i += 1) {
      const slot = document.createElement('div');
      slot.className = 'bundle-slot';
      slot.dataset.slotIndex = String(i);

      slot.innerHTML = `
        <div class="bundle-slot__header">
          <span class="bundle-slot__number" aria-hidden="true">${i}</span>
          <div class="bundle-slot__heading">
            <h4 class="bundle-slot__title">${this.escapeHtml(this.itemLabel)} ${i}</h4>
            <p class="bundle-slot__summary" data-slot-summary>Choose color &amp; size</p>
          </div>
        </div>

        ${colorOptions.length ? this.renderOptionGroup('color', 'Color', colorOptions) : ''}
        ${sizeOptions.length ? this.renderOptionGroup('size', 'Size', sizeOptions) : ''}

        <p class="bundle-slot__message" hidden></p>
        <input type="hidden" class="bundle-variant-id" name="bundle-slot-${i}-variant-id">
      `;

      this.slotsContainer.appendChild(slot);
      this.bindSlot(slot);
      this.refreshAvailability(slot);
    }

    this.updateProgress();
  }

  renderOptionGroup(type, label, values) {
    const wrapperClass = type === 'color' ? 'bundle-colors' : 'bundle-sizes';

    const buttonsHtml = values
      .map((value) => {
        const safeValue = this.escapeHtml(value);

        if (type === 'color') {
          const image = this.colorSwatches.get(value);

          if (image) {
            return `
              <button
                type="button"
                class="bundle-option bundle-option--swatch"
                data-type="color"
                data-value="${safeValue}"
                aria-pressed="false"
                aria-label="${safeValue}"
                title="${safeValue}"
                style="--swatch-image: url('${this.escapeHtml(image)}')"
              >
                <span class="bundle-option__check" aria-hidden="true">
                  <svg viewBox="0 0 16 16" width="10" height="10">
                    <path d="M2 8.5l4 4 8-9" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
              </button>`;
          }

          /* No image on file for this color — safe text-pill fallback, never breaks. */
          return `
            <button type="button" class="bundle-option" data-type="color" data-value="${safeValue}" aria-pressed="false">
              ${safeValue}
            </button>`;
        }

        return `
          <button type="button" class="bundle-option bundle-option--size" data-type="size" data-value="${safeValue}" aria-pressed="false">
            ${safeValue}
          </button>`;
      })
      .join('');

    return `
      <div class="bundle-slot__group">
        <span class="bundle-slot__label">${label}</span>
        <div class="${wrapperClass}" role="group" aria-label="${label}">
          ${buttonsHtml}
        </div>
      </div>`;
  }

  /** Resolves option values by NAME (not a hardcoded option1/option2 index). */
  getOptionValues(name) {
    const index = this.optionNames.findIndex(
      (optionName) => optionName && optionName.toLowerCase() === name.toLowerCase()
    );
    if (index === -1) return [];

    const key = `option${index + 1}`;
    return [...new Set(this.variants.map((variant) => variant[key]).filter(Boolean))];
  }

  /** Each slot only ever queries/deselects buttons within itself, so slots never affect each other. */
  bindSlot(slot) {
    const buttons = slot.querySelectorAll('.bundle-option');

    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        if (button.disabled) return;

        const type = button.dataset.type;

        slot.querySelectorAll(`.bundle-option[data-type="${type}"]`).forEach((btn) => {
          btn.classList.remove('selected');
          btn.setAttribute('aria-pressed', 'false');
        });

        button.classList.add('selected');
        button.setAttribute('aria-pressed', 'true');

        this.refreshAvailability(slot);
        this.findVariant(slot);
        this.clearError();
      });
    });
  }

  /** Disables Color/Size combinations within a slot that don't resolve to an available variant. */
  refreshAvailability(slot) {
    const selectedColor = slot.querySelector('[data-type="color"].selected')?.dataset.value;
    const selectedSize = slot.querySelector('[data-type="size"].selected')?.dataset.value;

    slot.querySelectorAll('.bundle-option[data-type="color"]').forEach((btn) => {
      const value = btn.dataset.value;
      const isAvailable = this.variants.some(
        (variant) =>
          variant.option1 === value &&
          (!selectedSize || variant.option2 === selectedSize) &&
          variant.available
      );
      btn.disabled = !isAvailable;
      btn.classList.toggle('unavailable', !isAvailable);
    });

    slot.querySelectorAll('.bundle-option[data-type="size"]').forEach((btn) => {
      const value = btn.dataset.value;
      const isAvailable = this.variants.some(
        (variant) =>
          variant.option2 === value &&
          (!selectedColor || variant.option1 === selectedColor) &&
          variant.available
      );
      btn.disabled = !isAvailable;
      btn.classList.toggle('unavailable', !isAvailable);
    });
  }

  findVariant(slot) {
    const color = slot.querySelector('[data-type="color"].selected')?.dataset.value;
    const size = slot.querySelector('[data-type="size"].selected')?.dataset.value;
    const messageEl = slot.querySelector('.bundle-slot__message');
    const summaryEl = slot.querySelector('[data-slot-summary]');
    const hiddenInput = slot.querySelector('.bundle-variant-id');

    hiddenInput.value = '';

    const hasColorOptions = slot.querySelector('.bundle-colors');
    const hasSizeOptions = slot.querySelector('.bundle-sizes');

    const parts = [color, size].filter(Boolean);
    if (summaryEl) {
      summaryEl.textContent = parts.length ? parts.join(' · ') : 'Choose color & size';
    }

    if ((hasColorOptions && !color) || (hasSizeOptions && !size)) {
      messageEl.hidden = true;
      slot.classList.remove('bundle-slot--complete');
      this.updateProgress();
      return;
    }

    const variant = this.variants.find(
      (item) =>
        (!hasColorOptions || item.option1 === color) &&
        (!hasSizeOptions || item.option2 === size) &&
        item.available
    );

    if (variant) {
      hiddenInput.value = variant.id;
      messageEl.hidden = true;
      slot.classList.add('bundle-slot--complete');
    } else {
      messageEl.textContent = 'This combination is sold out.';
      messageEl.hidden = false;
      slot.classList.remove('bundle-slot--complete');
    }

    this.updateProgress();
  }

  /** Updates the "X of N items ready" progress line above the Add button. */
  updateProgress() {
    if (!this.progressEl || !this.slotsContainer) return;

    const slots = [...this.slotsContainer.querySelectorAll('.bundle-slot')];
    const completed = slots.filter((slot) => slot.querySelector('.bundle-variant-id')?.value).length;

    this.progressEl.textContent = `${completed} of ${slots.length} items ready`;
    this.progressEl.classList.toggle(
      'bundle-builder__progress--done',
      completed === slots.length && slots.length > 0
    );
  }

  validateSlots() {
    const slots = [...this.slotsContainer.querySelectorAll('.bundle-slot')];
    return slots.find((slot) => !slot.querySelector('.bundle-variant-id').value) || null;
  }

  async addBundle() {
    this.clearError();

    const invalidSlot = this.validateSlots();
    if (invalidSlot) {
      this.showError('Please select a color and size for every item in your bundle.');
      invalidSlot.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const items = [...this.slotsContainer.querySelectorAll('.bundle-variant-id')]
      .map((input) => input.value)
      .filter(Boolean)
      .map((id) => ({ id: Number(id), quantity: 1 }));

    this.addButton.disabled = true;
    this.addButton.classList.add('is-loading');
    const originalLabel = this.addButton.textContent;
    this.addButton.textContent = 'Adding…';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.description || 'Could not add bundle to cart.');
      }

      window.location.href = '/cart';
    } catch (error) {
      console.error('BundleBuilder: add to cart failed', error);
      this.showError(error.message || 'Something went wrong. Please try again.');
      this.addButton.disabled = false;
      this.addButton.classList.remove('is-loading');
      this.addButton.textContent = originalLabel;
    }
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  clearError() {
    if (!this.errorEl) return;
    this.errorEl.hidden = true;
    this.errorEl.textContent = '';
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', BundleBuilder);
}
